import { useState, useEffect } from 'react';
import { X, Star, ShoppingCart, Minus, Plus, ChevronLeft, ChevronRight, Shield, Truck, RotateCcw, Store } from 'lucide-react';
import { useMarketplace } from '../../context/MarketplaceContext';
import { getMerchantById } from '../../data/marketplaceData';
import ProductCard from './ProductCard';

export default function ProductDetailDrawer() {
  const {
    selectedProduct,
    setSelectedProduct,
    addToCart,
    setShowCart,
    getProductRecommendations,
    getCrossSellingRecommendations,
    trackProductView,
  } = useMarketplace();

  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [crossSelling, setCrossSelling] = useState([]);

  const product = selectedProduct;
  const merchant = product ? getMerchantById(product.merchantId) : null;

  // Track view and load recommendations when product changes
  useEffect(() => {
    if (product) {
      trackProductView(product.id);
      setQuantity(1);
      setCurrentImageIndex(0);
      setRecommendations(getProductRecommendations(product.id));
      setCrossSelling(getCrossSellingRecommendations(product.id));
    }
  }, [product, trackProductView, getProductRecommendations, getCrossSellingRecommendations]);

  const handleClose = () => {
    setSelectedProduct(null);
  };

  const handleAddToCart = () => {
    setIsAdding(true);
    addToCart(product.id, quantity);
    setTimeout(() => {
      setIsAdding(false);
      setShowCart(true);
      handleClose();
    }, 600);
  };

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, Math.min(prev + delta, product.stock)));
  };

  const handleImageNav = (direction) => {
    setCurrentImageIndex(prev => {
      const total = product.images.length;
      return direction === 'next'
        ? (prev + 1) % total
        : (prev - 1 + total) % total;
    });
  };

  if (!product) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white z-50 animate-slide-in-right overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="font-bold text-lg text-gray-900">Product Details</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Image Gallery */}
          <div className="relative aspect-square bg-gray-100">
            <img
              src={product.images[currentImageIndex]}
              alt={product.name}
              className="w-full h-full object-cover"
            />

            {/* Image Navigation */}
            {product.images.length > 1 && (
              <>
                <button
                  onClick={() => handleImageNav('prev')}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => handleImageNav('next')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white"
                >
                  <ChevronRight size={20} />
                </button>

                {/* Dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {product.images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentImageIndex ? 'bg-indigo-600 w-4' : 'bg-white/70'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Discount Badge */}
            {product.discount > 0 && (
              <div className="absolute top-4 left-4 bg-red-500 text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg">
                -{product.discount}% OFF
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="p-4 space-y-4">
            {/* Merchant */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <Store size={16} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Sold by</p>
                <p className="text-sm font-bold text-indigo-600">{merchant?.name}</p>
              </div>
              {merchant?.verified && (
                <span className="ml-auto bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  Verified
                </span>
              )}
            </div>

            {/* Title & Rating */}
            <div>
              <h1 className="text-xl font-black text-gray-900 leading-tight">
                {product.name}
              </h1>
              <div className="flex items-center gap-2 mt-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={16}
                      className={star <= Math.round(product.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}
                    />
                  ))}
                </div>
                <span className="text-sm font-bold text-gray-700">{product.rating}</span>
                <span className="text-sm text-gray-400">({product.reviewCount} reviews)</span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-black text-gray-900">${product.price.toFixed(2)}</span>
              {product.originalPrice > product.price && (
                <>
                  <span className="text-lg text-gray-400 line-through">${product.originalPrice.toFixed(2)}</span>
                  <span className="text-sm font-bold text-emerald-600">
                    Save ${(product.originalPrice - product.price).toFixed(2)}
                  </span>
                </>
              )}
            </div>

            {/* Stock */}
            <div className="flex items-center gap-2">
              {product.stock > 10 ? (
                <span className="text-sm text-emerald-600 font-bold">In Stock</span>
              ) : product.stock > 0 ? (
                <span className="text-sm text-amber-600 font-bold">Only {product.stock} left</span>
              ) : (
                <span className="text-sm text-red-600 font-bold">Out of Stock</span>
              )}
              <span className="text-sm text-gray-400">|</span>
              <span className="text-sm text-gray-500">{product.sales.toLocaleString()}+ sold</span>
            </div>

            {/* Description */}
            <div className="pt-2 border-t border-gray-100">
              <h3 className="font-bold text-gray-900 mb-2">Description</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              {product.tags.map(tag => (
                <span
                  key={tag}
                  className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-3 py-4 border-t border-gray-100">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto bg-indigo-100 rounded-full flex items-center justify-center">
                  <Shield size={18} className="text-indigo-600" />
                </div>
                <p className="text-[10px] font-bold text-gray-600 mt-1">Secure Payment</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto bg-indigo-100 rounded-full flex items-center justify-center">
                  <Truck size={18} className="text-indigo-600" />
                </div>
                <p className="text-[10px] font-bold text-gray-600 mt-1">Fast Shipping</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto bg-indigo-100 rounded-full flex items-center justify-center">
                  <RotateCcw size={18} className="text-indigo-600" />
                </div>
                <p className="text-[10px] font-bold text-gray-600 mt-1">Easy Returns</p>
              </div>
            </div>

            {/* Cross-selling Recommendations */}
            {crossSelling.length > 0 && (
              <div className="pt-4 border-t border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3">You May Also Like</h3>
                <p className="text-xs text-gray-500 mb-3">From other merchants</p>
                <div className="grid grid-cols-2 gap-3">
                  {crossSelling.slice(0, 4).map(rec => (
                    <ProductCard key={rec.id} product={rec} compact />
                  ))}
                </div>
              </div>
            )}

            {/* Similar Products */}
            {recommendations.length > 0 && (
              <div className="pt-4 border-t border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3">Similar Products</h3>
                <div className="grid grid-cols-2 gap-3">
                  {recommendations.slice(0, 4).map(rec => (
                    <ProductCard key={rec.id} product={rec} compact />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer - Add to Cart */}
        <div className="border-t border-gray-100 p-4 bg-white">
          <div className="flex items-center gap-4">
            {/* Quantity Selector */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => handleQuantityChange(-1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={quantity <= 1}
              >
                <Minus size={16} className={quantity <= 1 ? 'text-gray-300' : 'text-gray-600'} />
              </button>
              <span className="w-8 text-center font-bold text-gray-900">{quantity}</span>
              <button
                onClick={() => handleQuantityChange(1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={quantity >= product.stock}
              >
                <Plus size={16} className={quantity >= product.stock ? 'text-gray-300' : 'text-gray-600'} />
              </button>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={product.stock === 0 || isAdding}
              className={`flex-1 py-3 px-6 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all ${
                isAdding
                  ? 'bg-green-500'
                  : product.stock === 0
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98]'
              }`}
            >
              <ShoppingCart size={20} className={isAdding ? 'animate-bounce' : ''} />
              <span>
                {isAdding ? 'Added!' : `Add to Cart - $${(product.price * quantity).toFixed(2)}`}
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
